<template>
  <div ref="dropdownRef" class="relative w-full">
    <!-- Dropdown Trigger -->
    <div
      class="flex items-center justify-between w-full px-3 py-2 text-sm border border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      :class="{ 'px-0.5! py-0.5!': modelValue.length > 0 }"
      @click="toggleDropdown"
    >
      <div class="flex flex-wrap gap-2 min-h-[20px] flex-1">
        <span v-if="modelValue.length === 0" class="text-gray-600">
          {{ placeholder }}
        </span>
        <div
          v-for="(item, index) in displayItems"
          :key="getItemKey(item, index)"
          class="multiselect-tag"
        >
          <span class="truncate max-w-[120px]">{{ getItemLabel(item) }}</span>
          <button
            type="button"
            class="multiselect-tag-close"
            @click.stop="removeItem(item)"
          >
            <IconClose />
          </button>
        </div>
        <div v-if="modelValue.length > maxDisplayItems" class="multiselect-tag">
          <span>{{ modelValue.length - maxDisplayItems }} more</span>
          <button
            type="button"
            class="multiselect-tag-close"
            @click.stop="clearAll"
          >
            <IconClose />
          </button>
        </div>
      </div>
      <IconChevronDown
        class="w-4 h-4 text-gray-400 transition-transform duration-200 ml-2 mr-0.5 shrink-0"
        :class="{ 'rotate-180': isOpen }"
      />
    </div>

    <!-- Dropdown Options -->
    <div
      v-show="isOpen"
      class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
    >
      <div class="p-2">
        <div
          v-for="(item, index) in options"
          :key="getItemKey(item, index)"
          class="multiselect-option"
          @click="toggleItem(item)"
        >
          <div class="multiselect-checkbox-container">
            <input
              type="checkbox"
              :checked="isItemSelected(item)"
              class="multiselect-checkbox"
              :data-test-id="`multiselect-checkbox-${getItemKey(item, index)}`"
              @click.stop="toggleItem(item)"
            />
            <svg
              class="multiselect-checkbox-icon"
              :class="{
                'multiselect-checkbox-icon--checked': isItemSelected(item),
                'multiselect-checkbox-icon--unchecked': !isItemSelected(item),
              }"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="16 5 9 12 6 9"></polyline>
            </svg>
          </div>

          <label class="flex-1 text-sm text-gray-900 cursor-pointer">
            {{ getItemLabel(item) }}
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup generic="T">
import IconChevronDown from '~/components/icons/IconChevronDown.vue'
import IconClose from '~/components/icons/IconClose.vue'

interface Props<T> {
  modelValue: T[]
  options: T[]
  placeholder?: string
  maxDisplayItems?: number
  getItemLabel?: (item: T) => string
  getItemKey?: (item: T, index: number) => string | number
  isItemSelected?: (item: T) => boolean
}

const props = withDefaults(defineProps<Props<T>>(), {
  placeholder: 'Select options...',
  maxDisplayItems: 2,
  getItemLabel: (_item: T) => String(_item),
  getItemKey: (_item: T, index: number) => index,
  isItemSelected: (_item: T) => false,
})

const emit = defineEmits<{
  'update:modelValue': [value: T[]]
  'item-added': [item: T]
  'item-removed': [item: T]
  'clear-all': []
}>()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

// Close dropdown when clicking outside
onClickOutside(dropdownRef, () => {
  isOpen.value = false
})

const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}

const displayItems = computed(() => {
  return props.modelValue.slice(0, props.maxDisplayItems)
})

const toggleItem = (item: T) => {
  const index = props.modelValue.findIndex(
    (selected) => props.getItemKey(selected, 0) === props.getItemKey(item, 0)
  )

  if (index > -1) {
    const newValue = [...props.modelValue]
    newValue.splice(index, 1)
    emit('update:modelValue', newValue)
    emit('item-removed', item)
  } else {
    const newValue = [...props.modelValue, item]
    emit('update:modelValue', newValue)
    emit('item-added', item)
  }
}

const removeItem = (item: T) => {
  const index = props.modelValue.findIndex(
    (selected) => props.getItemKey(selected, 0) === props.getItemKey(item, 0)
  )

  if (index > -1) {
    const newValue = [...props.modelValue]
    newValue.splice(index, 1)
    emit('update:modelValue', newValue)
    emit('item-removed', item)
  }
}

const clearAll = () => {
  emit('update:modelValue', [])
  emit('clear-all')
}

const isItemSelected = (item: T) => {
  return props.modelValue.some(
    (selected) => props.getItemKey(selected, 0) === props.getItemKey(item, 0)
  )
}
</script>
<style scoped>
@reference '~/assets/css/tailwind.css';
.multiselect-option {
  @apply flex items-center px-3 py-2 rounded cursor-pointer;
}

.multiselect-option:hover {
  background-color: #f9fafb;
}

.multiselect-checkbox-container {
  @apply relative flex items-center justify-center mr-3;
}

.multiselect-checkbox {
  @apply h-4 w-4 border-gray-300 rounded appearance-none;
  @apply focus:ring-2 focus:ring-offset-0;
  border: 1px solid #d1d5db;
}

.multiselect-checkbox:checked {
  background-color: rgba(4, 122, 136, 1);
  border-color: rgba(4, 122, 136, 1);
}

.multiselect-checkbox-icon {
  @apply absolute w-4 h-4 pointer-events-none;
  color: white;
}

.multiselect-checkbox-icon--checked {
  @apply block;
  margin-top: 3px;
  margin-right: 2px;
}

.multiselect-checkbox-icon--unchecked {
  @apply hidden;
}

.multiselect-tag {
  @apply inline-flex items-center gap-1 px-2 py-1 text-center rounded-lg border;
  @apply text-primary;
  font-family: Lexend, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 125%;
  border-radius: 8px;
  @apply border-primary;
  background: rgba(241, 254, 255, 0.9);
  box-shadow: 0 4px 16px 0 rgba(31, 42, 55, 0.03);
}

.multiselect-tag-close {
  @apply ml-1;
  @apply text-primary;
  @apply w-4 h-4;
  @apply flex items-center justify-center;
  @apply rounded-full;
  @apply hover:bg-primary/10;
  @apply transition-colors duration-150;
}

.multiselect-tag-close:hover {
  @apply opacity-80;
}

.multiselect-tag-close svg {
  @apply w-2.5 h-2.5;
}
</style>
